import React, { useState, useEffect, useRef } from 'react';
import { Info, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { getLatestVideo, getVideoStatus, getLatestS3Image } from '../api/videoService.api.js';
import { getRotationDegrees } from '../../utils/cameraOrientation.js';
import { useOrientedMedia } from '../../utils/useOrientedMedia.js';
import TimelapseRapido from './TimelapseRapido';
import ProgresoTimelapse from './ProgresoTimelapse';

const ASSISTANT_ERROR_MSG = "En estos momentos no podemos mostrar el timelapse, pero podes encargarselo a nuestro asistente:";
const MIN_HOURS_REQUIRED = 24;
const INSUFFICIENT_DATA_MSG = "Para poder comenzar a generar timelapse, se debe tener 24hs de imagenes como minimo.";
const POLL_INTERVAL_MS = 5000;
// Si el pedido al motor no cambia de estado en este tiempo se da por trabado: se deja de mostrar la barra y el
// padre pasa al motor del Editor (o se queda con el timelapse rápido si el motor falla).
const TRABADO_EN_COLA_MS = 3 * 60 * 1000;
const TRABADO_ARMANDO_MS = 8 * 60 * 1000;
const trabadoMs = (status) => {
  const sim = process.env.NODE_ENV === 'development' && window.__lisualPreviewTrabadoMs;
  if (sim) return sim;
  return status === 'PROCESSING' ? TRABADO_ARMANDO_MS : TRABADO_EN_COLA_MS;
};

// Mientras no hay video (se arma, falló o se trabó) el cliente ve el "timelapse rápido" hecho en el navegador con
// las fotos de la obra; la barra de avance aparece sólo mientras el motor está armando el video de verdad.
// onFallo: si el video de siempre no se puede armar (falla o se traba), el padre lo arma con el motor del Editor.
const LisualTimelapsePlayer = ({ user, className = "", onFallo }) => {
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [latestImageUrl, setLatestImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [motor, setMotor] = useState(null); // { status, desde }: estado real del pedido al motor
  const [insufficientData, setInsufficientData] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const rotationDeg = getRotationDegrees(user?.orientation);
  const { containerRef, captureNaturalSize, display } = useOrientedMedia(rotationDeg);
  const orientedStyle = display?.style || { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' };
  // Rotating the <video> element with CSS rotates its native controls bar
  // along with it (sideways at 90/270, upside down at 180), so whenever we
  // rotate we swap the native bar for our own, laid out outside the
  // transform so it stays upright regardless of the video's rotation.
  const needsCustomControls = rotationDeg === 90 || rotationDeg === 180 || rotationDeg === 270;
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const hasEnoughHistory = () => {
    if (!user?.startDate) return true;
    const elapsedHours = (Date.now() - new Date(user.startDate).getTime()) / (1000 * 60 * 60);
    return elapsedHours >= MIN_HOURS_REQUIRED;
  };

  const fetchLatestImage = async () => {
    if (!user?.feedID) return;
    try {
      const imageData = await getLatestS3Image(user.feedID);
      const url = typeof imageData === 'string' ? imageData : imageData?.url;
      if (url) {
        // Preload S3 image to avoid "progressive" loading effect
        const img = new Image();
        img.src = url;
        img.onload = () => {
          setLatestImageUrl(url);
        };
      }
    } catch (err) {
      console.error("Error loading latest image placeholder:", err);
    }
  };

  const clearPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  };

  const fetchLatestVideo = async () => {
    if (!user?.feedID) {
      setError(ASSISTANT_ERROR_MSG);
      return;
    }

    if (!hasEnoughHistory()) {
      setInsufficientData(true);
      return;
    }

    setLoading(true);
    setError(null);
    setMotor(null);
    setInsufficientData(false);
    try {
      const data = await getLatestVideo(user.feedID);

      if (data.status === 'READY') {
        setVideoUrl(data.videoUrl);
        setLoading(false);
        return;
      }

      // status === 'PENDING'. El motor a veces devuelve el pedido ya FALLADO (p. ej. falta la primera imagen):
      // no hay nada que esperar.
      if (data.job?.status === 'FAILED') {
        setError(ASSISTANT_ERROR_MSG);
        setLoading(false);
      } else if (data.job?.id) {
        setMotor({ status: data.job.status || 'PENDING', desde: Date.now() });
        startPolling(data.job.id, data.job.status || 'PENDING');
      } else {
        setError(ASSISTANT_ERROR_MSG);
        setLoading(false);
      }
    } catch (err) {
      console.error("Error fetching latest video:", err);
      setError(ASSISTANT_ERROR_MSG);
      setLoading(false);
    }
  };

  const startPolling = (jobId, statusInicial) => {
    clearPolling();
    let ultimo = { status: statusInicial, desde: Date.now() };

    pollIntervalRef.current = setInterval(async () => {
      try {
        const currentJob = await getVideoStatus(jobId);
        const st = currentJob?.status;

        if (st === 'COMPLETED' && currentJob.videoUrl) {
          setVideoUrl(currentJob.videoUrl);
          setMotor(null);
          setLoading(false);
          clearPolling();
        } else if (st === 'FAILED' || st === 'COMPLETED') {
          // Nunca el texto del motor (failureReason es interno): el padre arma el video de otra forma.
          setError(ASSISTANT_ERROR_MSG);
          setMotor(null);
          setLoading(false);
          clearPolling();
        } else {
          if (st && st !== ultimo.status) {
            ultimo = { status: st, desde: Date.now() };
            setMotor(ultimo);
          } else if (Date.now() - ultimo.desde > trabadoMs(ultimo.status)) {
            setError(ASSISTANT_ERROR_MSG);
            setMotor(null);
            setLoading(false);
            clearPolling();
          }
        }
      } catch (err) {
        console.error("Error polling video status:", err);
        setError(ASSISTANT_ERROR_MSG);
        setMotor(null);
        setLoading(false);
        clearPolling();
      }
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    if (error && onFallo) onFallo();
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * duration;
    setCurrentTime(v.currentTime);
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // <video key={videoUrl}> remounts on a new url, but this state doesn't
  // reset on its own since it lives on the parent, not the video node.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [videoUrl]);

  useEffect(() => {
    if (user?.feedID) {
      fetchLatestImage();
      fetchLatestVideo();
    }
    return () => {
      clearPolling();
      if (latestImageUrl && latestImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(latestImageUrl);
      }
    };
  }, [user?.feedID, user?.sourceType]);

  if (insufficientData) {
    return (
      <div ref={containerRef} className={`relative flex flex-col items-center justify-center rounded-lg aspect-video ${className} overflow-hidden ${display?.isVertical ? 'bg-black' : ''}`}>
        {latestImageUrl ? (
          <img
            src={latestImageUrl}
            alt="Vista actual de la obra"
            onLoad={(e) => captureNaturalSize(e.target)}
            style={orientedStyle}
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gray-200" />
        )}

        <div className="absolute inset-0 bg-black/50 z-0" />

        <div className="relative z-10 flex flex-col items-center justify-center p-4 text-center">
          <Info className="w-10 h-10 text-white mb-3" />
          <p className="text-sm text-white font-medium px-4 drop-shadow-md">
            {INSUFFICIENT_DATA_MSG}
          </p>
        </div>
      </div>
    );
  }

  // Sin video (armándose, falló o se trabó): el timelapse rápido con las fotos de la obra, nunca un cartel técnico.
  if (error) {
    return (
      <div ref={containerRef} className={`relative rounded-lg overflow-hidden aspect-video ${className} ${display?.isVertical ? 'bg-black' : 'bg-gray-200'}`}>
        <TimelapseRapido feedId={user?.feedID} imgStyle={orientedStyle} onImgLoad={(e) => captureNaturalSize(e.target)} />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative rounded-lg overflow-hidden aspect-video ${className} ${display?.isVertical ? 'bg-black' : 'bg-gray-200'}`}>
      {/* Background: Latest Image Placeholder or Video */}
      {videoUrl ? (
        <>
          <video
            key={videoUrl}
            ref={videoRef}
            src={videoUrl}
            controls={!needsCustomControls}
            onLoadedMetadata={(e) => {
              captureNaturalSize(e.target);
              setDuration(e.target.duration || 0);
            }}
            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            style={orientedStyle}
            autoPlay={false}
          />
          {needsCustomControls && (
            <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center gap-2 bg-black/70 px-3 py-2">
              <button type="button" onClick={togglePlay} className="text-white shrink-0 [@media(pointer:coarse)]:p-2.5 [@media(pointer:coarse)]:-m-2">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <span className="text-white text-xs tabular-nums shrink-0">{formatTime(currentTime)}</span>
              <div
                className="relative flex-1 h-1.5 bg-white/30 rounded-full cursor-pointer"
                onClick={handleSeek}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-white rounded-full"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
              <span className="text-white text-xs tabular-nums shrink-0">{formatTime(duration)}</span>
              <button type="button" onClick={toggleMute} className="text-white shrink-0 [@media(pointer:coarse)]:p-3 [@media(pointer:coarse)]:-m-2.5">
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </div>
          )}
        </>
      ) : (
        // Mientras se busca o se arma el video: las fotos de la obra de fondo y, si el motor lo está armando, la barra en el
        // medio (porcentaje y cuánto falta). Sin rótulos chicos arriba.
        <>
          <TimelapseRapido feedId={user?.feedID} imgStyle={orientedStyle} onImgLoad={(e) => captureNaturalSize(e.target)} />
          {loading && motor && <ProgresoTimelapse desde={motor.desde} estimadoSeg={150} texto="Armando el timelapse en alta calidad" nota="" />}
        </>
      )}
    </div>
  );
};

export default LisualTimelapsePlayer;
